/**
 * ComingSoonCard — the public roadmap, in-app. Gives users a reason to stick
 * around (and answers "why don't you do cold email?" before support does).
 * Keep in sync with the marketing site's #roadmap section.
 */

import { useNavigate } from 'react-router-dom'
import {
  Mail,
  Send,
  Twitter,
  Building2,
  ImageIcon,
  MessageCircle,
  Webhook,
  Users,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

type RoadmapItem = {
  readonly label: string
  readonly description: string
  readonly icon: typeof Mail
  readonly state: 'new' | 'soon' | 'planned'
}

const ROADMAP: readonly RoadmapItem[] = [
  {
    label: 'AI Image Posts',
    description: 'Generate images with your own fal.ai or Kie.ai key and publish them with your LinkedIn posts. Live for Pro in Settings, Integrations.',
    icon: ImageIcon,
    state: 'new',
  },
  {
    label: 'Cold Email Sequences',
    description: 'The same pipeline, over email: sent from your own Gmail or Outlook with open and reply tracking.',
    icon: Mail,
    state: 'soon',
  },
  {
    label: 'Instantly and Smartlead Sync',
    description: 'Push DealCloser leads straight into the cold email campaigns you already run. Key connection is live for Pro today.',
    icon: Send,
    state: 'soon',
  },
  {
    label: 'GoHighLevel CRM Sync',
    description: 'Replied and qualified leads flow into your GHL pipelines automatically. Key connection is live for Pro today.',
    icon: Building2,
    state: 'soon',
  },
  {
    label: 'X (Twitter) Publishing',
    description: 'Cross post your content engine output to X on the same schedule.',
    icon: Twitter,
    state: 'planned',
  },
  {
    label: 'WhatsApp and Instagram Outreach',
    description: 'More channels on the same autopilot and inbox.',
    icon: MessageCircle,
    state: 'planned',
  },
  {
    label: 'Zapier and Webhooks',
    description: 'Trigger anything in your stack when a lead replies or books a meeting.',
    icon: Webhook,
    state: 'planned',
  },
  {
    label: 'Team Seats',
    description: 'Run several LinkedIn accounts under one workspace with shared reporting.',
    icon: Users,
    state: 'planned',
  },
]

const STATE_BADGE: Record<RoadmapItem['state'], { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-emerald-500/15 text-emerald-400 border-transparent' },
  soon: { label: 'Coming soon', className: 'bg-blue-500/15 text-blue-400 border-transparent' },
  planned: { label: 'Planned', className: 'bg-muted text-muted-foreground border-transparent' },
}

export function ComingSoonCard() {
  const navigate = useNavigate()
  return (
    <Card>
      <CardHeader>
        <CardTitle>Coming Soon</CardTitle>
        <CardDescription>
          What we are building next. Want something prioritized? Tell us on the{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground"
            onClick={() => navigate('/support')}
          >
            Support page
          </button>
          .
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {ROADMAP.map((item) => {
            const Icon = item.icon
            const badge = STATE_BADGE[item.state]
            return (
              <div key={item.label} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="outline" className={`text-[10px] ${badge.className}`}>
                    {badge.label}
                  </Badge>
                </div>
                <p className="mt-2 text-sm font-medium">{item.label}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {item.description}
                </p>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
